// SPDX-License-Identifier: AGPL-3.0-or-later

import {buildCustomEmojiURL} from '@app/features/expressions/utils/CustomEmojiImageUrl';
import type {GuildEmoji as WireGuildEmoji} from '@fluxer/schema/src/domains/guild/GuildEmojiSchemas';
import type {UserPartial} from '@fluxer/schema/src/domains/user/UserResponseSchemas';

const EMPTY_TAG_IDS: ReadonlyArray<string> = Object.freeze([]);
const EMPTY_KEYWORDS: ReadonlyArray<string> = Object.freeze([]);

function stringArraysEqual(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

export class GuildEmoji {
	readonly id: string;
	readonly guildId: string;
	readonly name: string;
	readonly uniqueName: string;
	readonly allNamesString: string;
	readonly url: string;
	readonly animated: boolean;
	readonly categoryId: string | null;
	readonly tagIds: ReadonlyArray<string>;
	readonly keywords: ReadonlyArray<string>;
	readonly position: number | null;
	readonly user?: UserPartial;

	constructor(guildId: string, data: WireGuildEmoji, keywords: ReadonlyArray<string> = EMPTY_KEYWORDS) {
		this.id = data.id;
		this.guildId = guildId;
		this.name = data.name;
		this.uniqueName = data.name;
		this.allNamesString = `:${data.name}:`;
		this.url = buildCustomEmojiURL({id: data.id, animated: data.animated});
		this.animated = data.animated;
		this.categoryId = data.category_id ?? null;
		this.tagIds = data.tag_ids ?? EMPTY_TAG_IDS;
		this.keywords = keywords;
		this.position = data.position ?? null;
		this.user = data.user;
	}

	withUpdates(updates: Partial<WireGuildEmoji>, keywords: ReadonlyArray<string> = this.keywords): GuildEmoji {
		return new GuildEmoji(
			this.guildId,
			{
				id: updates.id ?? this.id,
				name: updates.name ?? this.name,
				animated: updates.animated ?? this.animated,
				category_id: updates.category_id !== undefined ? updates.category_id : this.categoryId,
				tag_ids: updates.tag_ids !== undefined ? updates.tag_ids : this.tagIds,
				position: updates.position !== undefined ? updates.position : this.position,
				user: updates.user ?? this.user,
			},
			keywords,
		);
	}

	equals(other: GuildEmoji): boolean {
		return (
			this.id === other.id &&
			this.guildId === other.guildId &&
			this.name === other.name &&
			this.animated === other.animated &&
			this.categoryId === other.categoryId &&
			this.position === other.position &&
			stringArraysEqual(this.tagIds, other.tagIds) &&
			stringArraysEqual(this.keywords, other.keywords) &&
			this.user?.id === other.user?.id
		);
	}

	toJSON(): WireGuildEmoji {
		return {
			id: this.id,
			name: this.name,
			animated: this.animated,
			category_id: this.categoryId,
			tag_ids: this.tagIds,
			position: this.position,
			user: this.user,
		};
	}

	static create(guildId: string, data: WireGuildEmoji, keywords: ReadonlyArray<string> = EMPTY_KEYWORDS): GuildEmoji {
		return new GuildEmoji(guildId, data, keywords);
	}
}
